import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AppProvider, useAppContext } from './contexts/AppContext';
import Navbar from './components/Navbar';
import SearchBar from './components/SearchBar';
import FoodCard from './components/FoodCard';
import { translations, getTranslatedCategory } from './utils/translations';

// Main app content component
const AppContent = () => {
  const { darkMode, search, language } = useAppContext();
  const [fodmapData, setFodmapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const t = translations[language];

  useEffect(() => {
    const loadFodmapData = async () => {
      try {
        const response = await fetch('/fodmap.json');
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const json = await response.json();
        const data = Array.isArray(json) ? json.filter(food => food.category !== 'Low FODMAP Certified Meals') : [];
        setFodmapData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadFodmapData();
  }, []);

  const filteredData = fodmapData.filter(food => {
    if (!search) return true;
    
    try {
      const regex = new RegExp(search, 'i');
      const nameToSearch = language === 'sk' ? food.name_sk : food.name;
      const translatedCategory = getTranslatedCategory(food.category, language);
      
      return regex.test(nameToSearch) || 
             regex.test(food.category) || 
             regex.test(translatedCategory);
    } catch (error) {
      // If regex is invalid, fall back to simple string matching
      const nameToSearch = language === 'sk' ? food.name_sk : food.name;
      const translatedCategory = getTranslatedCategory(food.category, language);
      
      return nameToSearch.toLowerCase().includes(search.toLowerCase()) ||
             food.category.toLowerCase().includes(search.toLowerCase()) ||
             translatedCategory.toLowerCase().includes(search.toLowerCase());
    }
  });

  if (loading) return (
    <div className={darkMode ? 'bg-dark text-light min-vh-100' : 'bg-light min-vh-100'}>
      <Navbar />
      <div className="container pt-5">
        <div className="alert alert-info">{t.loading}</div>
      </div>
    </div>
  );
  
  if (error) return (
    <div className={darkMode ? 'bg-dark text-light min-vh-100' : 'bg-light min-vh-100'}>
      <Navbar />
      <div className="container pt-5">
        <div className="alert alert-danger">{t.error} {error}</div>
      </div>
    </div>
  );

  return (
    <div className={darkMode ? 'bg-dark text-light min-vh-100' : 'bg-light min-vh-100'}>
      <Navbar />
      
      <div className="container mt-4">
        <SearchBar />
        
        <p className="text-center">
          {t.showingResults} <strong>{filteredData.length}</strong> {t.foodsText}
        </p>
        
        <div className="row">
          {filteredData.map(food => (
            <FoodCard key={food.id} food={food} />
          ))}
        </div>
      </div>
    </div>
  );
};

// Main App component with provider
const App = () => {
  return (
    <AppProvider>
      <AppContent />
    </AppProvider>
  );
};

export default App;
